import { FC, useMemo, useState } from "react";
import { Account, Asset, MsgExecute, MsgMulti, MsgSend } from "rujira.js";
import { Button, SwapSelect } from "rujira.ui";
import {
  BalanceCompact,
  usePreloadedBalance,
} from "../../common/components/Balance";
import {
  MsgProvider,
  TxBalanceAccountButton,
  TxButton,
} from "../../common/components/TxButton";
import { useAccounts } from "../../services/accounts";
import { ChunkSlider } from "../../trade/components/InputQuantity";
import { AdjustmentSummary } from "./Common";
import { CdpSingle } from "./utils";

export const Increase: FC<{
  hideModal: () => void;
  p: CdpSingle<Asset>;
  registry: string;
}> = ({ hideModal, registry, p }) => {
  const { selected } = useAccounts();
  const [debtAmount, setDebtAmount] = useState<bigint>(0n);
  const [collateralAmount, setCollateralAmount] = useState<bigint>(0n);
  const q = CdpSingle.depositAndBorrow(p, collateralAmount, debtAmount);
  const collateralBalance = usePreloadedBalance();
  const debtMax = q.debt.maxAdjust - p.debt.amount;

  const msg = useMemo(() => {
    if (!selected) return null;
    if (
      debtAmount === p.debt.amount &&
      collateralAmount === p.collateral.amount
    )
      return null;
    const debtDenom = p.debt.asset.variants?.native?.denom;
    const collateralDenom = p.collateral.asset.variants?.native?.denom;
    const acc: Account = { address: selected.address, network: "THOR" };

    return new MsgMulti(
      acc,
      [
        collateralDenom && collateralAmount
          ? new MsgSend(
              acc,
              p.collateral.asset,
              collateralAmount,
              p.account.address
            )
          : null,
        debtDenom && debtAmount
          ? new MsgExecute(acc, [], registry, {
              account: {
                addr: p.account.address,
                msgs: [
                  {
                    borrow: { denom: debtDenom, amount: debtAmount.toString() },
                  },
                  {
                    send: {
                      to_address: selected.address,
                      funds: [
                        { denom: debtDenom, amount: debtAmount.toString() },
                      ],
                    },
                  },
                ],
              },
            })
          : null,
      ].filter((x) => !!x)
    );
  }, [selected, debtAmount, collateralAmount]);

  return (
    <MsgProvider msg={msg}>
      <div style={{ marginLeft: "-0.5rem", marginRight: "-0.5rem" }}>
        <div className="row wrap gap-y-2 pad--sm mt-3">
          <div className="col-12 col-md-6">
            <h3 className="fs-14 fw-500 color-grey mb-1 ml-2">
              Deposit Collateral
            </h3>
            <BalanceCompact
              className="mb-0.5 px-1"
              onClick={(v) => setCollateralAmount(v.balance)}
            />

            <SwapSelect
              selected={p.collateral.asset}
              amount={collateralAmount}
              onChangeAmount={setCollateralAmount}
              full
              className="bg-black"
            />

            <div className="flex ai-c mt-1.5 mx-2">
              <a
                className="fs-12 condensed fw-500 color-grey mr-0.5 pointer"
                onClick={() => setDebtAmount(0n)}>
                0%
              </a>
              <ChunkSlider
                amount={collateralAmount}
                setAmount={setCollateralAmount}
                max={collateralBalance.balance?.balance || 0n}
              />
              <a
                className="fs-12 condensed fw-500 color-grey ml-0.5 pointer"
                onClick={() =>
                  setCollateralAmount(collateralBalance.balance?.balance || 0n)
                }>
                100%
              </a>
            </div>
          </div>
          <div className="col-12 col-md-6">
            <h3 className="fs-14 fw-500 color-grey mb-1 mb-md-3.5 ml-2">
              Increase Loan
            </h3>
            <SwapSelect
              selected={p.debt.asset}
              amount={debtAmount}
              onChangeAmount={setDebtAmount}
              full
              max={debtMax}
              className="bg-black"
            />

            <div className="flex ai-c mt-1.5 mx-2">
              <a
                className="fs-12 condensed fw-500 color-grey mr-0.5 pointer"
                onClick={() => setDebtAmount(0n)}>
                0%
              </a>
              <ChunkSlider
                amount={debtAmount}
                setAmount={setDebtAmount}
                max={debtMax}
              />
              <a
                className="fs-12 condensed fw-500 color-grey ml-0.5 pointer"
                onClick={() => setDebtAmount(debtMax)}>
                100%
              </a>
            </div>
          </div>

          <AdjustmentSummary p={q} />
        </div>
      </div>
      <div className="modal__footer mt-4 px-3 py-2 flex ai-s jc-e wrap">
        <Button
          className="button--grey button--outline"
          onClick={hideModal}
          label="Cancel"
        />
        <div className="block ml-a text-right">
          {collateralAmount ? (
            <TxBalanceAccountButton
              required={{ amount: collateralAmount, asset: p.collateral.asset }}
              label="Increase Loan"
            />
          ) : (
            <TxButton label="Increase Loan" />
          )}
        </div>
      </div>
    </MsgProvider>
  );
};
